import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Check, MessageCircle, Star, Mail } from 'lucide-react';
import { trackPixelEvent } from './MetaPixel';
import PhoneInput from './PhoneInput';
import { IconButton } from './UI';

type Lang = 'pt' | 'en' | 'es';

export interface PublicQuizQuestion {
  key: string;
  i18n?: Record<string, { text?: string }>;
  options: { key: string; i18n?: Record<string, { label?: string }> }[];
}

export interface PublicQuiz {
  enabled: boolean;
  questions: PublicQuizQuestion[];
}

interface QuizResult {
  outcome: 'vip' | 'qualified' | 'nurture';
  title?: string;
  message?: string;
  whatsappGroupUrl?: string;
  // Já havia lead com este e-mail/WhatsApp nesta LP: o backend devolve o resultado anterior.
  alreadySubmitted?: boolean;
}

interface Props {
  slug: string;
  quiz: PublicQuiz;
  lang: Lang;
  tracking: {
    utm: Record<string, string>;
    fbclid: string;
    gclid: string;
  };
  onClose: () => void;
}

const pick = (i18n: Record<string, any> | undefined, lang: Lang, field: string): string =>
  i18n?.[lang]?.[field] || i18n?.pt?.[field] || '';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Quiz de qualificação em tela cheia: uma pergunta por tela → contato → resultado. O resultado
// (e o link do grupo) vem do servidor; aqui não existe regra nenhuma de qualificação.
const LandingQuiz: React.FC<Props> = ({ slug, quiz, lang, tracking, onClose }) => {
  const { t } = useTranslation();
  const questions = quiz.questions;
  const [step, setStep] = useState(0); // 0..n-1 perguntas, n = contato
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [name, setName] = useState('');
  const [whatsapp, setWhatsapp] = useState(''); // E.164, montado pelo PhoneInput
  const [whatsappValid, setWhatsappValid] = useState(false);
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<QuizResult | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const advancingRef = useRef(false);

  const isContactStep = step >= questions.length;
  const total = questions.length + 1;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  useEffect(() => {
    dialogRef.current?.scrollTo({ top: 0 });
  }, [step, result]);

  const choose = (questionKey: string, optionKey: string) => {
    // Evita pular duas perguntas num toque duplo durante o atraso abaixo.
    if (advancingRef.current) return;
    advancingRef.current = true;
    setAnswers((prev) => ({ ...prev, [questionKey]: optionKey }));
    // Pequeno atraso para o toque/realce da opção escolhida ser percebido antes de avançar.
    window.setTimeout(() => {
      setStep((s) => s + 1);
      advancingRef.current = false;
    }, 180);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) return setError(t('landingQuiz.errorName'));
    if (!whatsappValid) return setError(t('landingQuiz.errorWhatsapp'));
    if (!EMAIL_RE.test(email.trim())) return setError(t('landingQuiz.errorEmail'));
    if (!consent) return setError(t('landingQuiz.errorConsent'));

    try {
      setSubmitting(true);
      const res = await axios.post(`/landing-pages/${slug}/quiz`, {
        answers,
        name: name.trim(),
        whatsapp,
        email: email.trim(),
        consent: true,
        lang,
        utm: tracking.utm,
        fbclid: tracking.fbclid,
        gclid: tracking.gclid,
        referrer: document.referrer
      });
      const data: QuizResult = res.data;
      setResult(data);
      // Reenvio do mesmo cliente não é lead novo — não contar de novo no pixel.
      if (data.alreadySubmitted) return;
      trackPixelEvent('Lead', { content_name: slug, content_category: `quiz_${data.outcome}` });
      if (data.outcome !== 'nurture') {
        trackPixelEvent('CompleteRegistration', { content_name: slug, status: data.outcome });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('landingQuiz.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  };

  const qualified = result && result.outcome !== 'nurture';
  const resultTitle = result?.title || (qualified ? t('landingQuiz.fallbackQualifiedTitle') : t('landingQuiz.fallbackNurtureTitle'));
  const resultMessage = result?.message || (qualified ? t('landingQuiz.fallbackQualifiedMessage') : t('landingQuiz.fallbackNurtureMessage'));

  const inputCls = 'w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-primary-500 focus:border-transparent';

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-stretch sm:items-center justify-center sm:p-4" role="dialog" aria-modal="true">
      <div ref={dialogRef} className="bg-white w-full sm:max-w-lg sm:rounded-2xl shadow-2xl overflow-y-auto max-h-full flex flex-col">
        {/* Topo: voltar / progresso / fechar */}
        <div className="sticky top-0 bg-white z-10 px-4 pt-4 pb-3 border-b border-gray-100">
          <div className="flex items-center justify-between gap-3">
            {!result && step > 0 ? (
              <IconButton action="back" label={t('landingQuiz.back')} onClick={() => setStep((s) => s - 1)} className="-ml-2" />
            ) : (
              <span className="w-9" />
            )}
            {!result && (
              <span className="text-xs font-medium text-gray-500">
                {t('landingQuiz.progress', { current: Math.min(step + 1, total), total })}
              </span>
            )}
            <IconButton action="close" label={t('landingQuiz.close')} onClick={onClose} className="-mr-2" />
          </div>
          {!result && (
            <div className="mt-3 h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-600 transition-all duration-300"
                style={{ width: `${(Math.min(step, total) / total) * 100}%` }}
              />
            </div>
          )}
        </div>

        <div className="px-5 sm:px-8 py-6 sm:py-8 flex-1">
          {result ? (
            <div className="text-center">
              <div className={`mx-auto mb-5 w-16 h-16 rounded-full flex items-center justify-center ${qualified ? 'bg-green-100' : 'bg-primary-50'}`}>
                {qualified ? <Check className="w-8 h-8 text-green-600" /> : <Mail className="w-8 h-8 text-primary-600" />}
              </div>
              {result.outcome === 'vip' && (
                <span className="inline-flex items-center gap-1 mb-3 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold">
                  <Star className="w-3.5 h-3.5" /> {t('landingQuiz.vipBadge')}
                </span>
              )}
              {result.alreadySubmitted && (
                <p className="mb-3 text-sm text-gray-500">{t('landingQuiz.alreadySubmitted')}</p>
              )}
              <h2 className="text-2xl font-bold text-gray-900 mb-3">{resultTitle}</h2>
              <p className="text-gray-600 mb-8">{resultMessage}</p>
              {result.whatsappGroupUrl ? (
                <a
                  href={result.whatsappGroupUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 w-full sm:w-auto bg-[#25D366] hover:bg-[#1ebe5b] text-white font-semibold text-lg py-4 px-8 rounded-lg shadow-lg transition-colors"
                >
                  <MessageCircle className="w-5 h-5" />
                  {result.outcome === 'vip'
                    ? t('landingQuiz.joinVipGroup')
                    : result.outcome === 'nurture' ? t('landingQuiz.joinNurtureGroup') : t('landingQuiz.joinGroup')}
                </a>
              ) : (
                <button type="button" onClick={onClose} className="btn-primary w-full sm:w-auto px-8 py-3">
                  {t('landingQuiz.done')}
                </button>
              )}
            </div>
          ) : isContactStep ? (
            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">{t('landingQuiz.contactTitle')}</h2>
                <p className="text-sm text-gray-600">{t('landingQuiz.contactSubtitle')}</p>
              </div>
              <input type="text" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('landingQuiz.name')} aria-label={t('landingQuiz.name')} className={inputCls} />
              <PhoneInput
                lang={lang}
                onChange={(e164, valid) => { setWhatsapp(e164); setWhatsappValid(valid); }}
                placeholder={t('landingQuiz.whatsappPh')}
                ariaLabel={t('landingQuiz.whatsapp')}
                countryAriaLabel={t('landingQuiz.country')}
              />
              <input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('landingQuiz.email')} aria-label={t('landingQuiz.email')} className={inputCls} />
              <label className="flex items-start gap-3 text-sm text-gray-600 cursor-pointer">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                <span>
                  {t('landingQuiz.consent')}{' '}
                  <Link to="/privacidade" target="_blank" className="underline text-primary-600">{t('landingQuiz.consentLink')}</Link>{' '}
                  {t('landingQuiz.consentSuffix')}
                </span>
              </label>
              {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
              <button type="submit" disabled={submitting} className="btn-primary w-full py-4 text-lg disabled:opacity-60">
                {submitting ? t('landingQuiz.submitting') : t('landingQuiz.submit')}
              </button>
            </form>
          ) : (
            (() => {
              const q = questions[step];
              return (
                <div key={q.key}>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">{pick(q.i18n, lang, 'text')}</h2>
                  <div className="space-y-3">
                    {q.options.map((o) => {
                      const selected = answers[q.key] === o.key;
                      return (
                        <button
                          key={o.key}
                          type="button"
                          onClick={() => choose(q.key, o.key)}
                          className={`w-full text-left px-5 py-4 rounded-xl border-2 text-base font-medium transition-colors duration-150 flex items-center justify-between gap-3 ${selected ? 'border-primary-600 bg-primary-50 text-primary-800' : 'border-gray-200 hover:border-primary-400 text-gray-800'}`}
                        >
                          <span>{pick(o.i18n, lang, 'label')}</span>
                          {selected && <Check className="w-5 h-5 text-primary-600 shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()
          )}
        </div>
      </div>
    </div>
  );
};

export default LandingQuiz;
